import React from 'react';
import Link from 'next/link';
import { IFirstHandReport } from '@/api/resort/types';
import { IWebcamItem } from '@/api/region/types';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { formatAgoTimestampOrString } from '@/util/formatAgo';
import { ResortWebcam } from '@/components/common/ResortWebcam';
import { Text } from '@/components/ui/text';
import { MiniSectionHeading } from './atoms';

export const FhrAndWebcam = ({
  firstHand, featured, allFhrHref, allCamsHref,
}: {
  firstHand: IFirstHandReport | null;
  featured: IWebcamItem | null;
  allFhrHref: string;
  allCamsHref: string;
}) => {
  const { t } = useOTSTranslation('common');
  const hasFhr = firstHand && (firstHand.largeImage || firstHand.image);

  if (!hasFhr && !featured) return null;

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
      {hasFhr && (
        <section>
          <MiniSectionHeading
            title={t('resort.skiReport.firstHandReport')}
            href={allFhrHref}
            ctaLabel={t('resort.skiReport.allFirstHandReports')}
          />
          <Link href={allFhrHref} className="group block">
            <div className="relative aspect-[5/4] overflow-hidden rounded-md bg-muted">
              <img
                src={firstHand!.largeImage ?? firstHand!.image ?? ''}
                alt={firstHand!.name || t('resort.skiReport.firstHandReport')}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
              />
              {/* Date overlay — matches the timestamp pill on the
                  webcam card beside this one. */}
              <span className="absolute bottom-3 right-3 rounded bg-black/65 px-2 py-0.5 text-xs font-medium text-white">
                {formatAgoTimestampOrString(firstHand!.date)}
              </span>
            </div>
            {firstHand!.name && (
              <div className="mt-2 text-base font-bold leading-tight text-foreground group-hover:text-primary">
                {firstHand!.name}
              </div>
            )}
            {(firstHand!.translated || firstHand!.body) && (
              <Text variant="default" className="mt-1 line-clamp-3 text-sm italic text-muted-foreground">
                {firstHand!.translated || firstHand!.body}
              </Text>
            )}
          </Link>
        </section>
      )}

      {featured && (
        <section>
          <MiniSectionHeading
            title={t('resort.cams.heroTitle')}
            href={allCamsHref}
            ctaLabel={t('common.seeAllCams')}
          />
          <ResortWebcam
            caption={featured.title}
            image={featured.image ?? ''}
            timestamp={formatAgoTimestampOrString(featured.date)}
            href={allCamsHref}
            aspect="4/3"
          />
        </section>
      )}
    </div>
  );
};
